import React, { useEffect } from 'react';
import { Box, SelectChangeEvent } from '@mui/material';
import { useRecoilState } from 'recoil';
import { sendableMorphingState } from 'features/morphing/state/morphingAtoms';
import { parameterListState, Vector3 } from 'features/morphing/state/parameterAtoms';
import {
  parameterNameState,
  stageParameterState,
  selectedParameterNameState
} from 'features/morphing/state/stageParameterAtoms';
import { useResetValues } from 'features/morphing/utils/resetValues';
import { NewParameterNameInput } from './subcomponents/NewParameterNameInput';
import { ParameterSelectDropdown } from './subcomponents/ParameterSelectDropdown';
import { FFDActionButtons } from './subcomponents/FFDActionButtons';
import { isNaturalVector } from 'shared/utils/vectorCheck';
import { isValidSlice } from 'shared/utils/sliceCheck';
import { isValidParameterSpace } from 'shared/utils/parameterSpaceCheck';

const customSx = {
  backgroundColor: '#1e1e1e',
  display: 'flex',
  gap: 0.5,
  paddingBottom: 0.5,
  justifyContent: 'space-around',
  width: '100%'
};

const getMorphingLength = (
  morphingMin: Vector3,
  morphingMax: Vector3
) => {
  return {
    x: morphingMax.x - morphingMin.x,
    y: morphingMax.y - morphingMin.y,
    z: morphingMax.z - morphingMin.z
  };
};

export const ParameterManager: React.FC = () => {
  const [parameterList, setParameterList] = useRecoilState(parameterListState);
  const [stageParameter, setStageParameter] = useRecoilState(stageParameterState);
  const [selectedParameterName, setSelectedParameterName] = useRecoilState(selectedParameterNameState);
  const [newParameterName, setNewParameterName] = useRecoilState(parameterNameState);
  const [sendable, setSendable] = useRecoilState(sendableMorphingState);

  useEffect(() => {
    const isControlPointsValid = isNaturalVector(stageParameter.controlPoints);
    const morphingLength: Vector3 = getMorphingLength(stageParameter.morphingMin, stageParameter.morphingMax);
    const isMorphingLengthValid = isNaturalVector(morphingLength);
    const isTargetControlPointsXValid = isValidSlice(stageParameter.targetControlPointsX, stageParameter.controlPoints.x);
    const isTargetControlPointsYValid = isValidSlice(stageParameter.targetControlPointsY, stageParameter.controlPoints.y);
    const isTargetControlPointsZValid = isValidSlice(stageParameter.targetControlPointsZ, stageParameter.controlPoints.z);
    const isMorphingParameterValid = isValidParameterSpace(stageParameter.morphingParameter);
    setSendable(
      isControlPointsValid &&
      isMorphingLengthValid &&
      isTargetControlPointsXValid &&
      isTargetControlPointsYValid &&
      isTargetControlPointsZValid &&
      isMorphingParameterValid
    );
  }, [stageParameter, setSendable]);

  const resetValues = useResetValues();

  // ドロップダウンの変更
  const handleParameterChange = (event: SelectChangeEvent<string>) => {
    const selectedName = event.target.value as string
    const selectedParameter = parameterList.find(config => config.name === selectedName);
    if (selectedParameter) {
      setSelectedParameterName(selectedName);
      setStageParameter(selectedParameter);
    }
  };

  // morphing parameterの追加
  const addParameter = () => {
    const isDuplicate = parameterList.find(config => config.name === newParameterName);
    if (isDuplicate) {
      alert("既に同じ名前のconfigが存在します。\n既存のconfigを削除、あるいはnew config nameを変更して下さい。")
    } else if (newParameterName === '') {
      alert("new config nameが空です。");
    } else {
      setParameterList((prevList) => [...prevList, stageParameter]);
      setNewParameterName('');
      setSelectedParameterName(newParameterName);
    }
  };

  // morphing parameterの削除
  const removeParameter = () => {
    setParameterList((prevList) =>
      prevList.filter((config) => config.name !== selectedParameterName)
    );
    setSelectedParameterName('');
  };

  return (
    <Box component="div">
      <Box component="div" sx={customSx}>
        <NewParameterNameInput
          newParameterName={newParameterName}
          setNewParameterName={setNewParameterName}
        />
        <ParameterSelectDropdown
          selectedParameterName={selectedParameterName}
          parameterList={parameterList}
          handleParameterChange={handleParameterChange}
        />
      </Box>

      <Box component="div" sx={customSx}>
        <FFDActionButtons
          addParameter={addParameter}
          removeParameter={removeParameter}
          resetValues={resetValues}
          sendable={sendable}
        />
      </Box>
    </Box>
  );
};
